import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Logo } from "@/components/AppShell";

export const input = "mt-1.5 w-full h-12 px-4 rounded-xl border border-stone-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#C86D51] focus:border-transparent";

export const Frame = ({ title, sub, children, testid, img }) => (
  <div className={`grain min-h-screen ${img ? "lg:grid lg:grid-cols-2" : ""}`}>
    {img && <div className="hidden lg:block relative"><img src={img} alt="A quiet lake beach at dawn" className="absolute inset-0 h-full w-full object-cover" /></div>}
    <div className="relative z-10 max-w-sm mx-auto px-6 py-16 w-full lg:self-center" data-testid={testid}>
      <Link to="/" className="block mb-12"><Logo /></Link>
      <h1 className="font-display text-3xl sm:text-4xl tracking-tight">{title}</h1>
      <p className="text-stone-600 mt-2 mb-8">{sub}</p>
      {children}
      <Link to="/login" data-testid="reset-back-login" className="tap block text-sm text-[#C86D51] font-semibold mt-6">Back to sign in</Link>
    </div>
  </div>
);

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { setSent((await api.post("/auth/forgot-password", { email })).data.message); } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  return (
    <Frame title="Forgot your password?" sub="Enter your email and we'll send you a link to choose a new one." testid="forgot-page">
      {sent ? <p className="card p-4 text-sm text-stone-700" data-testid="forgot-sent">{sent} Check your inbox (and spam). The link works once, for 30 minutes.</p> : (
        <form onSubmit={submit} className="space-y-4">
          <label className="block"><span className="text-sm font-medium text-stone-700">Email</span>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email-input" className={input} autoComplete="email" /></label>
          <button disabled={busy} data-testid="forgot-submit-btn" className="btn-ink w-full h-12 rounded-full font-semibold disabled:opacity-60">{busy ? "Sending…" : "Send reset link"}</button>
        </form>
      )}
    </Frame>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const token = params.get("token") || "";
  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) return setError("Those passwords don't match.");
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      toast.success("Password updated. Please sign in.");
      nav("/login");
    } catch (err) { setError(errMsg(err)); setBusy(false); }
  };
  return (
    <Frame title={token ? "Choose a new password" : "This reset link is incomplete"} sub={token ? "For your security, this signs you out on every device." : "Reset links only work when opened in full from the email."} testid="reset-page">
      {!token ? (
        <div className="space-y-4" data-testid="reset-missing-token-block">
          <p className="text-sm text-red-700" data-testid="reset-missing-token">This link is incomplete. Please request a new one.</p>
          <a href="/forgot-password" data-testid="reset-request-new" className="btn-ink w-full h-12 rounded-full font-semibold inline-flex items-center justify-center">Request a new link</a>
        </div>
      ) : <form onSubmit={submit} className="space-y-4">
        <label className="block"><span className="text-sm font-medium text-stone-700">New password</span>
          <input type="password" required minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-password-input" className={input} autoComplete="new-password" placeholder="At least 8 characters" /></label>
        <label className="block"><span className="text-sm font-medium text-stone-700">Confirm password</span>
          <input type="password" required minLength={8} value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="reset-password-confirm-input" className={input} autoComplete="new-password" /></label>
        {error && <p data-testid="reset-error" className="text-sm text-red-700 bg-red-50 rounded-lg px-3 py-2">{error}</p>}
        <button disabled={busy} data-testid="reset-submit-btn" className="btn-ink w-full h-12 rounded-full font-semibold disabled:opacity-60">{busy ? "Saving…" : "Save new password"}</button>
      </form>}
    </Frame>
  );
}
